import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  onReminderAction, onReminderSyncRequest, registerReminderActions, reminderTarget, remindersSupported, requestReminderSync, syncReminders,
} from '../lib/reminders';
import { useApp, useAppPath } from '../lib/AppContext';

// In the iPhone app, keeps the phone's reminders in step with the appointments and settings: on start, when
// the app comes back to the front, and whenever a screen asks (requestReminderSync). A reminder's buttons
// open the matching screen; "Đã tập" / "Không tập" answer first, then open that day. Renders nothing.
export default function ReminderSync() {
  const { api, reminders } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const running = useRef(null);
  const again = useRef(false);

  useEffect(() => {
    if (!remindersSupported()) return undefined;
    // One sync at a time; a request during a sync runs one more after it.
    const sync = () => {
      if (running.current) { again.current = true; return; }
      running.current = syncReminders(api, reminders).catch(() => {}).finally(() => {
        running.current = null;
        if (again.current) { again.current = false; sync(); }
      });
    };
    sync();
    const onVisible = () => document.visibilityState === 'visible' && sync();
    document.addEventListener('visibilitychange', onVisible);
    const stop = onReminderSyncRequest(sync);
    return () => { document.removeEventListener('visibilitychange', onVisible); stop(); };
  }, [api, reminders]);

  // Registered once; the latest navigate is read from a ref.
  const go = useRef(null);
  go.current = (to) => navigate(path(to));
  const apiRef = useRef(api);
  apiRef.current = api;
  useEffect(() => {
    if (!remindersSupported()) return undefined;
    registerReminderActions().catch(() => {});
    let remove = () => {};
    let cancelled = false;
    onReminderAction((actionId, extra) => {
      const open = () => go.current(reminderTarget(actionId, extra));
      if (extra.kind !== 'checkin' || !['trained', 'missed'].includes(actionId)) return open();
      return apiRef.current.post(`/appointments/${extra.appointment_id}/attendance`, { attendance: actionId })
        .catch(() => {}).finally(() => { requestReminderSync(); open(); });
    })
      .then((fn) => { if (cancelled) fn(); else remove = fn; });
    return () => { cancelled = true; remove(); };
  }, []);

  return null;
}
